import { formatParisDateTime, type OrderDetail } from '@flux/shared';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { TimelineEntryRow } from '@/components/ui/timeline.js';

type OrderError = OrderDetail['errors'][number];

function ErrorRow({ error }: { error: OrderError }): JSX.Element {
  return (
    <li className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-xs font-medium tabular-nums text-muted-foreground">
          {formatParisDateTime(new Date(error.at))}
        </span>
        <span className="font-mono text-xs text-muted-foreground">{error.step}</span>
      </div>
      <p className="break-words text-sm text-status-red-fg">{error.message}</p>
      {error.detail ? (
        <details className="mt-0.5">
          <summary className="w-fit cursor-pointer rounded-sm text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            Détail technique
          </summary>
          <pre className="mt-1.5 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs text-muted-foreground">
            {error.detail}
          </pre>
        </details>
      ) : null}
    </li>
  );
}

/** Onglet 4 « Timeline » (`02` §4) : événements horodatés puis erreurs techniques détaillées. */
export function TimelineTab({ order }: { order: OrderDetail }): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <PageSection title="Événements">
        {order.timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun événement enregistré.</p>
        ) : (
          <ol className="flex flex-col">
            {order.timeline.map((entry, index) => (
              <TimelineEntryRow key={`${entry.at}-${String(index)}`} entry={entry} />
            ))}
          </ol>
        )}
      </PageSection>

      {order.errors.length > 0 ? (
        <PageSection
          title={`Erreurs techniques${order.attempts > 0 ? ` (${String(order.attempts)} tentative${order.attempts > 1 ? 's' : ''})` : ''}`}
        >
          <ul className="flex flex-col divide-y divide-border">
            {order.errors.map((error, index) => (
              <ErrorRow key={`${error.at}-${String(index)}`} error={error} />
            ))}
          </ul>
        </PageSection>
      ) : null}
    </div>
  );
}
